/**
 * Generic E-Signing Form Validation
 */
export default class ESigningFormValidation {
    constructor() {
        this.regexPersonalNumber = /^\d{8}-\d{4}$/;
        this.regexMobileNumber = /^\+46[0-9]{9}$/;
    }

    /**
     * @description Validate step 1 (Template and Document)
     * @returns boolean
     */
    validateStep1() {
        const signingTemplate = document.getElementById('signingTemplate').value;
        const documentFiles = document.getElementById('documentFile').files;

        if (!signingTemplate || signingTemplate.trim() === '') {
            this.showError(eSigningTranslation.signingTemplateRequired);
            return false;
        }

        if (documentFiles.length === 0) {
            this.showError(eSigningTranslation.agreementFileRequired);
            return false;
        }

        // Validate file type
        for (let file of documentFiles) {
            if (file.type !== 'application/pdf') {
                this.showError(eSigningTranslation.agreementFileValidateFileType);
                return false;
            }
        }

        return true;
    }

    /**
     * @description Validate email format
     * @param {string} email - Email address
     * @returns boolean
     */
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * @description Validate mobile number format
     * @param {string} phone - Phone number
     * @returns boolean
     */
    isValidMobileNumber(phone) {
        return this.regexMobileNumber.test(phone);
    }

    /**
     * @description Validate personal identity number format (YYYYMMDD-XXXX)
     * @param {string} personalNumber - Personal number
     * @returns boolean
     */
    isValidPersonalNumber(personalNumber) {
        if (!this.regexPersonalNumber.test(personalNumber)) {
            return false;
        }

        const [datePart, serialPart] = personalNumber.split('-');
        if (!this.isValidDatePart(datePart)) {
            return false;
        }

        // Luhn is calculated on YYMMDDXXXX (last 10 digits)
        const tenDigitPersonalNumber = `${datePart.substring(2)}${serialPart}`;
        return this.isValidLuhn(tenDigitPersonalNumber);
    }

    /**
     * @description Validate YYYYMMDD date part
     * @param {string} datePart - Date in YYYYMMDD format
     * @returns boolean
     */
    isValidDatePart(datePart) {
        const year = parseInt(datePart.substring(0, 4), 10);
        const month = parseInt(datePart.substring(4, 6), 10);
        const day = parseInt(datePart.substring(6, 8), 10);

        const date = new Date(year, month - 1, day);

        return (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day
        );
    }

    /**
     * @description Validate Luhn checksum for a 10 digit string
     * @param {string} tenDigits
     * @returns boolean
     */
    isValidLuhn(tenDigits) {
        if (!/^\d{10}$/.test(tenDigits)) {
            return false;
        }

        let sum = 0;

        for (let i = 0; i < tenDigits.length; i++) {
            let digit = parseInt(tenDigits.charAt(i), 10);

            if (i % 2 === 0) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }

            sum += digit;
        }

        return sum % 10 === 0;
    }

    /**
     * @description Show validation error
     * @param {string} message - Error message
     * @returns void
     */
    showError(message) {
        const visibleStep = document.querySelector('.steps:not(.hidden)');
        const errorElement =
            visibleStep?.querySelector('.validate-before-add-error-message') ||
            document.querySelector('.validate-before-add-error-message');

        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = 'block';

            // Auto-hide after 5 seconds
            setTimeout(() => {
                errorElement.style.display = 'none';
            }, 5000);
        }
    }

    /**
     * @description Show validation error for a specific input field
     * @param {string} inputId - Input element id
     * @param {string} message - Error message
     * @returns void
     */
    showFieldError(inputId, message) {
        const inputElement = document.getElementById(inputId);
        const errorElement = document.getElementById(`${inputId}-error`);

        if (!inputElement || !errorElement) {
            return;
        }

        errorElement.textContent = message;
        errorElement.style.display = 'block';
        inputElement.classList.add('input-validation-error');
    }

    /**
     * @description Clear validation error for a specific input field
     * @param {string} inputId - Input element id
     * @returns void
     */
    clearFieldError(inputId) {
        const inputElement = document.getElementById(inputId);
        const errorElement = document.getElementById(`${inputId}-error`);

        if (!inputElement || !errorElement) {
            return;
        }

        errorElement.textContent = '';
        errorElement.style.display = 'none';
        inputElement.classList.remove('input-validation-error');
    }

}
